<script lang="ts">
import type { PropType } from '@mimir/web/compat/vueCompat';
import { defineComponent } from '@mimir/web/compat/vueCompat';
import { VCol, VIcon, VRow, VTextField } from 'vuetify/lib';

type VariantType = 'underlined' | 'outlined' | 'filled' | 'solo' | 'plain';
type ValidateOnType = 'blur' | 'input' | 'submit';

const isVue2 = !!import.meta.env.VUE_APP_V2;

export default defineComponent({
  name: 'MTextField',
  components: {
    VCol,
    VIcon,
    VRow,
    VTextField,
  },
  inheritAttrs: false,
  props: {
    // Vue 2 prop
    value: {
      type: [String, Number] as PropType<string | number>,
      default: ''
    },
    // Vue 3 prop
    modelValue: {
      type: [String, Number] as PropType<string | number>,
      default: ''
    },
    label: {
      type: String as PropType<string>,
      default: undefined
    },
    validatedTooltip: {
      type: String as PropType<string>,
      default: undefined
    },
    prependIcon: {
      type: String as PropType<string>,
      default: undefined
    },
    tooltipPosition: {
      type: String as PropType<string>,
      default: undefined
    },
    validated: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    isPassword: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    showReveal: {
      type: Boolean as PropType<boolean>,
      default: true
    },
    variant: {
      type: String as PropType<VariantType>,
      default: 'solo'
    },
    clickable: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    type: {
      type: String as PropType<string>,
      default: 'text'
    },
    validateOn: {
      type: [String, Array] as PropType<ValidateOnType | ValidateOnType[]>,
      default: undefined
    }
  },
  data() {
    return {
      isFocused: false,
      showingSecret: false,
      isVue2
    };
  },
  computed: {
    internalValue: {
      get(): string | number {
        return this.modelValue !== '' ? this.modelValue : this.value;
      },
      set(val: string | number) {
        this.$emit('input', val); // Vue 2
        this.$emit('update:modelValue', val); // Vue 3
      }
    },
    computedFilled(): boolean {
      return this.variant === 'filled';
    },
    computedOutlined(): boolean {
      return this.variant === 'outlined';
    },
    computedSolo(): boolean {
      return this.variant === 'solo' || this.variant === 'plain';
    },
    computedPrependIcon(): string | undefined {
      return this.prependIcon || (this.$attrs['prepend-inner-icon'] as string);
    },
    computedValidateOnBlur(): boolean | undefined {
      if (this.validateOn) {
        const validateOnArray = Array.isArray(this.validateOn) ? this.validateOn : [this.validateOn];
        return validateOnArray.includes('blur');
      }
      return undefined;
    },
    slotNameMapping(): Record<string, string> {
      return {
        // v3 -> v2 mappings
        'prepend': 'prepend',
        'prepend-inner': 'prepend',
        'append-inner': 'append',
        'append': 'append-outer',
      };
    },
    transformedScopedSlots(): Record<string, any> {
      if (!isVue2) return {};

      const transformed: Record<string, any> = {};
      for (const slotName in this.$scopedSlots) {
        if (slotName === 'infotext') {
          transformed[slotName] = this.$scopedSlots[slotName];
        } else {
          const v2SlotName = this.slotNameMapping[slotName] || slotName;
          transformed[v2SlotName] = this.$scopedSlots[slotName];
        }
      }
      return transformed;
    },
    transformedSlots(): Record<string, any> {
      if (!isVue2) return {};

      const transformed: Record<string, any> = {};
      for (const slotName in this.$slots) {
        if (slotName === 'infotext') {
          transformed[slotName] = this.$slots[slotName];
        } else {
          const v2SlotName = this.slotNameMapping[slotName] || slotName;
          transformed[v2SlotName] = this.$slots[slotName];
        }
      }
      return transformed;
    },
    vue2Bindings(): Record<string, any> {
      const bindings: Record<string, any> = {
        ...this.$attrs,
        value: this.internalValue,
        filled: this.computedFilled,
        outlined: this.computedOutlined,
        solo: this.computedSolo,
      };

      if (this.computedValidateOnBlur !== undefined) {
        bindings['validate-on-blur'] = this.computedValidateOnBlur;
      }

      delete bindings.variant;
      delete bindings.validateOn;
      delete bindings['validate-on'];
      delete bindings['prepend-inner-icon'];
      delete bindings.density;
      delete bindings.success;
      delete bindings['success-messages'];

      return bindings;
    },
    vue2Listeners(): Record<string, any> {
      const listeners: Record<string, any> = {
        ...this.$listeners,
        input: (value: any) => { this.internalValue = value; }
      };

      if (listeners['click:append']) {
        listeners['click:append-inner'] = listeners['click:append'];
        delete listeners['click:append'];
      }

      return listeners;
    },
    proxyType(): string {
      if (!this.isPassword) {
        return this.type;
      }
      return this.showingSecret ? this.type : 'password';
    },
    showPasswordTooltip(): string {
      return this.showingSecret ? 'Hide' : 'Reveal';
    }
  },
  methods: {
    focus() {
      (this.$refs.inner as any).focus();
    },
    getOriginalSlotName(v2SlotName: string): string {
      for (const [originalName, mappedName] of Object.entries(this.slotNameMapping)) {
        if (mappedName === v2SlotName && (this.$scopedSlots[originalName] || this.$slots[originalName])) {
          return originalName;
        }
      }
      return v2SlotName;
    }
  },
});

</script>

<template>
  <VRow
    no-gutters
    class="profile-row"
  >
    <VCol
      v-if="label && !computedFilled"
      cols="12"
      :class="['profile-label', isFocused && 'focused', computedPrependIcon && 'pl-8']"
    >
      <span
        v-if="!$scopedSlots['infotext']"
        v-text="label"
      />
      <InfoBubble
        v-else
        :tooltipPosition="tooltipPosition"
      >
        <span v-text="label" />
        <template #infotext>
          <slot name="infotext" />
        </template>
      </InfoBubble>
    </VCol>
    <VCol cols="12">
      <!-- Vue 2 Mode: Accept v3 props, transform to v2 for VTextField -->
      <VTextField
        v-if="isVue2"
        v-bind="vue2Bindings"
        ref="inner"
        :class="['m-text-field', 'm-common', clickable && 'clickable']"
        hide-details="auto"
        autocomplete="new-password"
        flat
        :type="proxyType"
        :loader-height="1"
        :prepend-icon="computedPrependIcon"
        :aria-label="$attrs['aria-label'] || label"
        :label="computedFilled ? label : ''"
        v-on="vue2Listeners"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
        <template
          v-if="isPassword && showReveal"
          #append
        >
          <VIcon
            v-tooltip="showPasswordTooltip"
            class="mr-1"
            style="cursor:pointer"
            @click="showingSecret = !showingSecret"
          >{{ showingSecret ? 'visibility_off' : 'visibility' }}</VIcon>
          <VIcon
            v-if="validated"
            v-tooltip="{ disabled: !validatedTooltip, text: validatedTooltip }"
            color="success"
            class="ml-1"
          >check</VIcon>
        </template>

        <template
          v-for="(_, scopedSlotName) in transformedScopedSlots"
          #[scopedSlotName]="slotData"
        >
          <slot
            :name="getOriginalSlotName(scopedSlotName)"
            v-bind="slotData"
          />
        </template>
        <template
          v-for="(_, slotName) in transformedSlots"
          #[slotName]
        >
          <slot :name="getOriginalSlotName(slotName)" />
        </template>
      </VTextField>

      <!-- Vue 3 Mode: Use native v3 props and slots directly -->
      <VTextField
        v-else
        v-bind="$attrs"
        ref="inner"
        :class="['m-text-field', 'm-common', clickable && 'clickable']"
        hide-details="auto"
        autocomplete="new-password"
        :type="proxyType"
        :variant="variant"
        :prepend-icon="prependIcon"
        :model-value="internalValue"
        :validate-on="validateOn"
        :aria-label="$attrs['aria-label'] || label"
        :label="computedFilled ? label : ''"
        @change="$emit('change', $event.target?.value ?? internalValue)"
        @update:model-value="internalValue = $event"
        @focus="isFocused = true"
        @blur="isFocused = false"
      >
        <template
          v-if="isPassword && showReveal"
          #append-inner
        >
          <VIcon
            v-tooltip="showPasswordTooltip"
            class="mr-1"
            style="cursor:pointer"
            @click="showingSecret = !showingSecret"
          >{{ showingSecret ? 'visibility_off' : 'visibility' }}</VIcon>
          <VIcon
            v-if="validated"
            v-tooltip="{ disabled: !validatedTooltip, text: validatedTooltip }"
            color="success"
            class="ml-1"
          >check</VIcon>
        </template>
        <template
          v-for="(_, slotName) in $slots"
          #[slotName]="slotData"
        >
          <slot
            :name="slotName"
            v-bind="slotData || {}"
          />
        </template>
      </VTextField>
    </VCol>
  </VRow>
</template>

<style scoped lang="stylus">
:deep(.v-input--is-disabled) .v-input__slot,
:deep(.v-input--is-readonly):not(.clickable) .v-input__slot
  opacity: .5
  input
    color: var(--v-default-base)
  .profile-row
    font-size: 14px
    white-space: pre-wrap
    overflow-wrap: break-word
    .profile-label
      font-size: 12px
      color: var(--v-primary-base)
      opacity: 0.8
      &.focused,
      .high-contrast
        color: var(--v-strong-base)
        opacity: 1
.clickable
  :deep(input)
    cursor: pointer
:deep(.v-text-field--filled)
  .v-input__append-inner
    margin-top: 7px
  .v-input__prepend-outer
    margin-top: 15px
  .v-input__slot
    padding: 0px 4px !important
    min-height: 36px !important
    border-radius: 2px !important
    background: var(--v-ternaryBackground-base) !important
    input
      color: var(--v-default-base)
  .v-label
    color: var(--v-default-base)
    top: 11px
    font-size: 13px !important
    line-height: 16px
  .v-label--active
    color: var(--v-default-base)
    top: 9px
    font-size: 11px !important
  input
    padding: 0 !important
    margin-top: 10px !important
    max-height: 36px !important
  .v-input__slot:before, .v-input__slot:after
    display: none
</style>
